/**
 * Remove heavy promo "View Details" HTML from list payloads so it is not
 * serialized into the initial page/Nuxt payload (load on click instead).
 */

type StrapiEntry = {
  id?: number | string
  attributes?: Record<string, unknown> | null
  [key: string]: unknown
}

type PageResponse = {
  data?: {
    attributes?: {
      blocks?: Array<Record<string, unknown>>
      [key: string]: unknown
    } | null
    [key: string]: unknown
  } | null
  [key: string]: unknown
}

const omitKeys = (attrs: Record<string, unknown> | null | undefined, keys: string[]) => {
  const entries = Object.entries(attrs ?? {}).filter(([key]) => !keys.includes(key))
  return Object.fromEntries(entries)
}

export const stripPulsePromoDetails = <T extends StrapiEntry>(entries: T[] | null | undefined): T[] => {
  if (!Array.isArray(entries)) return []
  return entries.map((entry) => ({
    ...entry,
    attributes: omitKeys(entry.attributes ?? undefined, ['discountDescription']),
  }))
}

export const stripCardPromoDetails = <T extends StrapiEntry>(entries: T[] | null | undefined): T[] => {
  if (!Array.isArray(entries)) return []
  return entries.map((entry) => ({
    ...entry,
    attributes: omitKeys(entry.attributes ?? undefined, ['longDescription']),
  }))
}

export const stripPromotionsPayload = (payload: {
  featuredPromo?: StrapiEntry[] | null
  promos?: StrapiEntry[] | null
  [key: string]: unknown
}) => {
  if (!payload || typeof payload !== 'object') return payload
  return {
    ...payload,
    featuredPromo: stripPulsePromoDetails(payload.featuredPromo),
    promos: stripCardPromoDetails(payload.promos),
  }
}

/** Strip longDescription from CMS `dynamic-blocks.card-promotions` page blocks. */
export const stripCardPromotionsFromPageBlocks = <T extends Record<string, unknown>>(blocks: T[]): T[] => {
  if (!Array.isArray(blocks)) return []
  return blocks.map((block) => {
    const component = block.component ?? block.__component
    if (component !== 'dynamic-blocks.card-promotions') return block

    const promotions = block.promotions as { data?: StrapiEntry[] } | undefined
    if (!promotions?.data || !Array.isArray(promotions.data)) return block

    return {
      ...block,
      promotions: {
        ...promotions,
        data: stripCardPromoDetails(promotions.data),
      },
    }
  })
}

/** Strip only Card Promotions details from a raw Strapi page response. */
export const stripCardPromotionsFromPageResponse = <T extends PageResponse>(payload: T): T => {
  const blocks = payload?.data?.attributes?.blocks
  if (!Array.isArray(blocks)) return payload

  return {
    ...payload,
    data: {
      ...payload.data,
      attributes: {
        ...payload.data?.attributes,
        blocks: stripCardPromotionsFromPageBlocks(blocks),
      },
    },
  } as T
}
